// Algorithmic "AI-style" palette generation for BrowserAiColorPaletteGenerator.
// Purely deterministic color math seeded by a caller-provided RNG. No backend,
// no model, no dependencies. Reuses HSL/RGB helpers from the color toolkit.

import { hslToRgb, rgbToHex, parseHex, rgbToHsl } from './browser-color-tools-extra';

// A recipe describes the hue/saturation/lightness envelope a palette is drawn
// from. Moods map keywords to a recipe; a seed color derives one on the fly.
export type PaletteRecipe = {
  baseHue: number; // 0-360
  hueSpread: number; // total degrees the 5 hues fan across
  sat: [number, number]; // saturation range, 0-100
  light: [number, number]; // lightness range, 0-100 (low slot -> high slot)
};

export type MoodDef = {
  key: string;
  label: string;
  keywords: string[];
  recipe: PaletteRecipe;
};

// Curated moods. Each maps a set of keyword synonyms to hue/sat/light rules.
export const MOODS: MoodDef[] = [
  {
    key: 'sunset',
    label: 'Sunset',
    keywords: ['sunset', 'dusk', 'warm', 'sunrise', 'golden', 'fire', 'autumn', 'fall'],
    recipe: { baseHue: 18, hueSpread: 70, sat: [70, 95], light: [45, 78] },
  },
  {
    key: 'forest',
    label: 'Forest',
    keywords: ['forest', 'green', 'nature', 'jungle', 'leaf', 'moss', 'woodland', 'spring'],
    recipe: { baseHue: 130, hueSpread: 60, sat: [35, 70], light: [28, 68] },
  },
  {
    key: 'ocean',
    label: 'Ocean',
    keywords: ['ocean', 'sea', 'water', 'blue', 'aqua', 'marine', 'sky', 'coast', 'wave'],
    recipe: { baseHue: 200, hueSpread: 65, sat: [45, 85], light: [35, 75] },
  },
  {
    key: 'pastel',
    label: 'Pastel',
    keywords: ['pastel', 'soft', 'baby', 'light', 'gentle', 'muted', 'cream', 'calm'],
    recipe: { baseHue: 320, hueSpread: 320, sat: [30, 55], light: [78, 90] },
  },
  {
    key: 'neon',
    label: 'Neon',
    keywords: ['neon', 'cyber', 'vivid', 'electric', 'bright', 'bold', 'pop', 'retro'],
    recipe: { baseHue: 285, hueSpread: 200, sat: [80, 100], light: [50, 65] },
  },
  {
    key: 'earth',
    label: 'Earth',
    keywords: ['earth', 'earthy', 'clay', 'terracotta', 'sand', 'desert', 'brown', 'rustic'],
    recipe: { baseHue: 30, hueSpread: 45, sat: [30, 55], light: [35, 72] },
  },
  {
    key: 'berry',
    label: 'Berry',
    keywords: ['berry', 'purple', 'plum', 'wine', 'grape', 'magenta', 'jewel', 'royal'],
    recipe: { baseHue: 320, hueSpread: 70, sat: [50, 85], light: [30, 68] },
  },
  {
    key: 'candy',
    label: 'Candy',
    keywords: ['candy', 'sweet', 'fun', 'playful', 'bubblegum', 'party', 'kids'],
    recipe: { baseHue: 340, hueSpread: 300, sat: [65, 90], light: [60, 78] },
  },
  {
    key: 'mono',
    label: 'Monochrome',
    keywords: ['mono', 'monochrome', 'grey', 'gray', 'minimal', 'noir', 'slate', 'stone'],
    recipe: { baseHue: 220, hueSpread: 20, sat: [6, 16], light: [22, 88] },
  },
  {
    key: 'vintage',
    label: 'Vintage',
    keywords: ['vintage', 'retro', 'sepia', 'faded', 'old', 'antique', 'nostalgic', 'film'],
    recipe: { baseHue: 40, hueSpread: 80, sat: [25, 50], light: [45, 80] },
  },
];

// Match a free-text keyword/mood to the best mood. Falls back to Ocean.
export function moodFromKeyword(input: string): MoodDef {
  const text = input.trim().toLowerCase();
  if (!text) return MOODS[2];
  for (const mood of MOODS) {
    if (mood.key === text) return mood;
    for (const kw of mood.keywords) {
      if (text === kw || text.includes(kw) || kw.includes(text)) return mood;
    }
  }
  return MOODS[2];
}

// Build a recipe from a seed color: keep its hue, spread analogously, and use
// its saturation/lightness as the centre of the envelope.
export function recipeFromSeed(hex: string): PaletteRecipe | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const { h, s, l } = rgbToHsl(rgb);
  const satLo = Math.max(10, s - 20);
  const satHi = Math.min(100, s + 15);
  const lightLo = Math.max(20, Math.min(l, 100 - l) < 25 ? l - 10 : l - 28);
  const lightHi = Math.min(92, lightLo + 45);
  return { baseHue: h, hueSpread: 55, sat: [satLo, satHi], light: [lightLo, lightHi] };
}

// A tiny seedable RNG so a given seed reproduces a palette (mulberry32).
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// Generate a cohesive 5-color palette from a recipe using the supplied RNG.
export function buildAiPalette(recipe: PaletteRecipe, rng: () => number): string[] {
  const [satLo, satHi] = recipe.sat;
  const [lightLo, lightHi] = recipe.light;
  return [0, 1, 2, 3, 4].map((i) => {
    const t = i / 4; // 0 -> 1 across the five slots
    const hueJitter = (rng() - 0.5) * 14;
    const h = recipe.baseHue + (t - 0.5) * recipe.hueSpread + hueJitter;
    const s = clamp(satLo + (satHi - satLo) * rng(), 0, 100);
    const lightJitter = (rng() - 0.5) * 8;
    const l = clamp(lightLo + (lightHi - lightLo) * t + lightJitter, 0, 100);
    return rgbToHex(hslToRgb({ h, s, l }));
  });
}

// Pick a text color (dark or light) that reads on a given background hex.
export function readableTextColor(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return '#111111';
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return yiq >= 140 ? '#111111' : '#FFFFFF';
}
